import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useProjects } from "@/hooks/useProjects";
import { formatDate } from "@/lib/date";

interface ProjectSettingsSectionProps {
  projectSlug: string;
}

export function ProjectSettingsSection({
  projectSlug,
}: ProjectSettingsSectionProps) {
  const { t } = useTranslation();
  const { projects, loading, error } = useProjects();

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t("projectPortal.config.settings.loading")}</span>
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">{error}</p>;
  }

  // SD-5: find by slug from the query-cached project list
  const project = projects.find((p) => p.slug === projectSlug);

  if (!project) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        {t("projectPortal.config.settings.empty")}
      </p>
    );
  }

  const rows: Array<{ label: string; value: string }> = [
    {
      label: t("projectPortal.config.settings.name"),
      value: project.name,
    },
    {
      label: t("projectPortal.config.settings.slug"),
      value: project.slug,
    },
    {
      label: t("projectPortal.config.settings.description"),
      value: project.description ?? "—",
    },
    {
      label: t("projectPortal.config.settings.created"),
      value: formatDate(project.created_at),
    },
    {
      label: t("projectPortal.config.settings.status"),
      value: project.is_active
        ? t("projectPortal.config.settings.active")
        : t("projectPortal.config.settings.inactive"),
    },
    {
      // SD-4: governance scope as static fallback row
      label: t("projectPortal.config.settings.governanceScope"),
      value: t("projectPortal.config.settings.notConfigured"),
    },
  ];

  return (
    <dl className="grid gap-2">
      {rows.map(({ label, value }) => (
        <div key={label} className="flex items-baseline gap-4 text-sm">
          <dt className="w-40 shrink-0 font-medium text-muted-foreground">
            {label}
          </dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
